import { useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { easeOut, Magnetic } from "./Shared";

const links = [
  { label: "Advisory", href: "#practice" },
  { label: "Mandates", href: "#mandates" },
  { label: "Voices", href: "#voices" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 1.2, ease: easeOut }}
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
          scrolled ? "bg-[#F4F3EF]/85 backdrop-blur-xl border-black/15" : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="#top" data-testid="navbar-logo" aria-label="I Putu Adi Semarada LLC — home" className="leading-tight group">
            <span className="block font-serif text-lg tracking-tight group-hover:text-[#D4512D] transition-colors duration-300">
              I PUTU ADI SEMARADA LLC
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="link-sweep font-mono text-[11px] uppercase tracking-[0.25em] text-black/70 hover:text-black transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <a
              href="tel:+18885576994"
              data-testid="nav-phone-link"
              className="hidden lg:flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] text-black/70 hover:text-[#D4512D] transition-colors duration-300"
            >
              <Phone size={13} strokeWidth={1.5} /> +1 888 557 6994
            </a>
            <Magnetic>
              <a
                href="#contact"
                data-testid="nav-cta-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-[#F4F3EF] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#D4512D]"
              >
                Consult <ArrowUpRight size={14} strokeWidth={1.5} />
              </a>
            </Magnetic>
          </nav>

          <button
            data-testid="menu-toggle-button"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-black"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#F4F3EF] flex flex-col justify-center px-8 md:hidden"
          >
            {links.map((l, i) => (
              <motion.a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: easeOut }}
                className="font-serif text-5xl py-4 border-b border-black/10 tracking-tight"
              >
                <span className="font-mono text-xs text-[#D4512D] mr-4 align-super">0{i + 1}</span>
                {l.label}
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
